import React, { useState } from 'react';
import { useAuth } from '../../../hooks';
import { ENV } from '../../../utils';

export const InterviewNotesScanner = ({ candidateId, onSaveNotes }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scannedResult, setScannedResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const { accessToken } = useAuth();

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
      setScannedResult(null);
      setErrorMsg("");
    }
  };

  const handleScan = async () => {
    if (!selectedFile) return;
    
    setIsScanning(true);
    setErrorMsg("");

    try {
      const formData = new FormData();
      formData.append("notesImage", selectedFile);

      const response = await fetch(`${ENV.BASE_API}/ai/analyze-notes`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Error al escanear las notas");
      }

      setScannedResult(data);
    } catch (error) {
      setErrorMsg(error.message || "Ocurrió un error al escanear la imagen.");
    } finally {
      setIsScanning(false);
    }
  };

  const [mode, setMode] = useState("ai"); // 'ai' or 'manual'
  const [manualNotes, setManualNotes] = useState("");

  const handleManualSave = () => {
    if (!manualNotes.trim()) return;
    onSaveNotes({
      transcripcion: manualNotes,
      puntuacion_entrevista: null,
      observaciones_clave: [],
      sentimiento_general: "N/A (Ingreso Manual)"
    });
    setManualNotes("");
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm mb-8 animate-fade-in">
      <div className="flex items-center justify-between mb-6 border-b border-gray-100 pb-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-1">Notas Internas de Entrevista</h3>
          <p className="text-sm text-gray-500">Agrega comentarios de la entrevista, usando IA para transcribir notas o escribiéndolas manualmente.</p>
        </div>
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button 
            onClick={() => setMode("ai")} 
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${mode === "ai" ? "bg-white text-[#151C84] shadow-sm font-bold" : "text-gray-500 hover:text-gray-700"}`}
          >
            Escáner IA
          </button>
          <button 
            onClick={() => setMode("manual")} 
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${mode === "manual" ? "bg-white text-[#151C84] shadow-sm font-bold" : "text-gray-500 hover:text-gray-700"}`}
          >
            Asignar Notas
          </button>
        </div>
      </div>

      {mode === "manual" ? (
        <div className="space-y-4 animate-fade-in">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Escribe tus observaciones</label>
            <textarea
              rows={5}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#151C84]/15 focus:border-[#151C84] transition-all outline-none resize-none"
              placeholder="Escribe aquí los comentarios y notas de la entrevista..."
              value={manualNotes}
              onChange={(e) => setManualNotes(e.target.value)}
            />
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleManualSave}
              disabled={!manualNotes.trim()}
              className="px-6 py-2 bg-[#151C84] text-white rounded-lg font-medium hover:bg-[#0d125c] disabled:opacity-50 transition-colors"
            >
              Guardar Notas
            </button>
          </div>
        </div>
      ) : !scannedResult ? (
        <div className="space-y-4 animate-fade-in">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:bg-gray-50 transition-colors">
            <input
              type="file"
              id="notes-upload"
              accept="image/jpeg, image/png, image/webp"
              className="hidden"
              onChange={handleFileChange}
            />
            <label htmlFor="notes-upload" className="cursor-pointer">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="block text-sm font-medium text-gray-900">
                {selectedFile ? selectedFile.name : "Seleccionar Fotografía"}
              </span>
              <span className="block text-xs text-gray-500 mt-1">PNG, JPG, WEBP hasta 5MB</span>
            </label>
          </div>

          {errorMsg && <p className="text-sm text-red-600 font-medium">{errorMsg}</p>}

          <div className="flex justify-end">
            <button
              onClick={handleScan}
              disabled={!selectedFile || isScanning}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                !selectedFile || isScanning 
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-[#151C84] text-white hover:bg-[#0d125c]'
              }`}
            >
              {isScanning ? 'Escaneando con IA...' : 'Analizar Notas'}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6 animate-fade-in">
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
            <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2">Análisis de la IA</h4>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Sentimiento General</p>
                <p className="font-medium text-gray-900">{scannedResult.sentimiento_general}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Puntuación Estimada</p>
                <p className="font-bold text-2xl text-gray-900">{scannedResult.puntuacion_entrevista}/100</p>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-1">Observaciones Clave</p>
              <ul className="list-disc pl-4 text-sm text-gray-800">
                {scannedResult.observaciones_clave?.map((obs, idx) => (
                  <li key={idx}>{obs}</li>
                ))}
              </ul>
            </div>

            <div>
              <p className="text-xs text-gray-500 mb-1">Transcripción Original</p>
              <p className="text-sm text-gray-700 bg-white p-3 border border-gray-200 rounded italic">
                "{scannedResult.transcripcion}"
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => setScannedResult(null)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Escanear Otra Imagen
            </button>
            <button
              onClick={() => onSaveNotes(scannedResult)}
              className="px-4 py-2 bg-[#151C84] text-white rounded-lg text-sm font-medium hover:bg-[#0d125c]"
            >
              Adjuntar al Perfil del Candidato
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
